import { useState, ChangeEvent, useEffect } from 'react';
import { InsuranceAppendix } from '../../../interfaces/InsuranceAppendix';
import { formatDate } from '../../../utils/formatDate';
import { FamilyMember } from '../../../interfaces/Client';
import { getClientFamilyMembers } from '../../../utils/getClientFamilyMembers';
import { deleteInsurancePolicy, editInsurancePolicy, restoreInsurancePolicy, generateInsurancePricing } from '../../../utils/api/insurancePolicy';
import { downloadFileFromAPI } from '../../../utils/downloadFileFromAPI';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InsuranceAppendixRecommendation } from '../../../interfaces/InsuranceAppendix';
import { deleteInsuranceAppendix } from '../../../utils/api/insuranceAppendix';
import CreateOrEditAppendixModal from './insuranceCreationAndEdit/AppendixCreationAndEditingModal';
import { createNewAppendix, editExistingAppendix } from './insuranceCreationAndEdit/appendixAndPolicyCreationFunctions';
import { useProductsContext } from './insuranceProductsContext';
import { useTranslation } from 'react-i18next';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import Swal from 'sweetalert2';
import EditableInput from '../../../components/EditableInput';
import { InsurancePolicy, InsurancePolicyMainBranch, InsurancePolicyPlanTypes, InsurancePolicyStatuses } from '../../../interfaces/InsurancePolicy';
import { FormInput } from '../../../components';
import InsurancePolicyModal from './insuranceCreationAndEdit/InsurancePolicyModal';
import { initialInsuranceAppendixState } from './insuranceCreationAndEdit/initialPolicyAndAppendixState';
import { set } from 'date-fns';
import { setIn } from 'immutable';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import AutoSavingInput from '../../../components/AutoSavingFormInput';
import Select from 'react-select';
import { Company } from '../../../interfaces/Company';
import { de } from 'date-fns/locale';
import { InsuranceRecommendation } from '../../../interfaces/Insurance';
import TranslatedColumn from '../../../components/TranslatedColumn';
import { addCommas } from '../../../utils/addCommas';
import { ClientFile } from '../../../interfaces/ClientFile';
import MoneyColumn from '../../../components/MoneyColumn';

const noDataFound = () => {
    return <>---</>;
};

const disabled = (policy: InsurancePolicy): boolean =>
    policy.planType !== InsurancePolicyPlanTypes.individual ||
    ![
        InsurancePolicyMainBranch.healthInsurance,
        InsurancePolicyMainBranch.lifeInsurance,
        InsurancePolicyMainBranch.workDisability,
        InsurancePolicyMainBranch.criticalIllness,
        InsurancePolicyMainBranch.mortgageInsurance,
    ].includes(policy.branch) ||
    policy.status !== InsurancePolicyStatuses.Initial;

interface PolicyHolderColumnProps {
    row: { original: InsurancePolicy };
    extraData?: any;
}

export const PolicyInsuredListColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    return <>{policy.insuredList?.map((person) => <div>{person.name}</div>)}</>;
};

export const PolicyNumberColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const file: ClientFile | undefined = policy.policyFile || policy.annualReports?.[0];
    return file ? (
        <a href={file.openLink || file.link} target="_blank" rel="noreferrer">
            {policy.policyNumber} <i className="mdi mdi-link"></i>
        </a>
    ) : (
        <>{policy.policyNumber}</>
    );
};

export const PolicyRecommendationColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const { consts, insurancePolicies, setInsurancePolicies } = useProductsContext();

    const { t } = useTranslation('insurance');

    if (policy.isNewPolicy) {
        return <>New</>;
    }

    const recommendationOptions = consts.InsuranceRecommendation;
    const recommendationReasonsOptions = consts.RecommendationReasons;

    if (!recommendationOptions || !recommendationReasonsOptions) {
        return noDataFound();
    }

    const handleRecommendationChange = async (option: any) => {
        const policiesToUpdate = (policy.isSelected &&
            insurancePolicies.filter((insurancePolicy) => insurancePolicy.isSelected).map((insurancePolicy) => insurancePolicy._id)) || [policy._id];
        const setPoliciesAgain = (recommendation: string) => {
            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                policiesToUpdate.includes(insurancePolicy._id) ? { ...insurancePolicy, recommendation } : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies as InsurancePolicy[]);
        };

        const newRecommendation = option.value;
        const oldRecommendation = policy.recommendation;
        if (newRecommendation === oldRecommendation) {
            return;
        }

        try {
            setPoliciesAgain(newRecommendation);
            for (const policyId of policiesToUpdate) {
                await editInsurancePolicy(policyId, {
                    recommendation: newRecommendation,
                });
            }
        } catch (error) {
            setPoliciesAgain(oldRecommendation);
            globalErrorHandler(error);
        }
    };

    return (
        <div className="d-flex gap-2">
            <Select
                isDisabled={disabled(policy)}
                styles={{
                    menu: (base) => ({
                        ...base,
                        position: 'absolute',
                        zIndex: 9999,
                    }),
                    container: (base) => ({
                        ...base,
                        width: '100%',
                    }),
                }}
                value={{
                    label: t(camelCaseToText(policy.recommendation)),
                    value: policy.recommendation,
                }}
                name="recommendation"
                options={Object.keys(recommendationOptions).map((recommendation) => ({
                    label: t(camelCaseToText(recommendation)),
                    value: recommendation as InsuranceRecommendation,
                }))}
                onChange={handleRecommendationChange}
            />
        </div>
    );
};

export const PolicyCompanyNameColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (!policy.company) {
        return noDataFound();
    }

    const company = policy.company as Company;

    if (company.thumbUrl) {
        return <img src={company.thumbUrl} alt={company.shortName || company.name} style={{ maxHeight: '35px', maxWidth: '150px' }} />;
    }

    return <>{company.shortName || company.name}</>;
};

export const PolicyMainBranchColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const { t } = useTranslation('insurance');
    if (!policy.branch) {
        return noDataFound();
    }

    let text =
        policy.branch === InsurancePolicyMainBranch.healthInsurance && policy.appendices?.length === 1
            ? policy.appendices[0].branch
            : t(camelCaseToText(policy.branch));

    return (
        <div>
            {text}
            <br />
            {t(policy.planType)}
        </div>
    );
};

export const PolicyPlanTypeColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (!policy.planType) {
        return noDataFound();
    }
    return <>{camelCaseToText(policy.planType)}</>;
};

export const AppendixTypeColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    if (!appendix.type) {
        return noDataFound();
    }
    return <>{appendix.type}</>;
};

export const PolicyIsAgentAppointedColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const { insurancePolicies, setInsurancePolicies } = useProductsContext();

    if (policy.isAgentAppointed === undefined) {
        return noDataFound();
    }

    if (policy.isNewPolicy) {
        return <></>;
    }

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const newIsAgentAppointed = event.target.checked;
        const prevValue = policy.isAgentAppointed;

        const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
            insurancePolicy._id === policy._id ? { ...insurancePolicy, isAgentAppointed: newIsAgentAppointed } : insurancePolicy,
        );
        setInsurancePolicies(newInsurancePolicies);

        try {
            await editInsurancePolicy(policy._id!, {
                isAgentAppointed: newIsAgentAppointed,
            });
        } catch (error) {
            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                insurancePolicy._id === policy._id ? { ...insurancePolicy, isAgentAppointed: prevValue } : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies);
            globalErrorHandler(error);
        }
    };

    return (
        <FormInput
            type="checkbox"
            name="isAgentAppointed"
            checked={policy.isAgentAppointed}
            disabled={
                disabled(policy) || ![InsuranceRecommendation.noChange, InsuranceRecommendation.partialCancellation].includes(policy.recommendation)
            }
            onChange={handleChange}
            onClick={(e: any) => e.stopPropagation()}
            className="d-flex align-items-center"
        />
    );
};

export const AppendixRecommendationColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    const [recommendation, setRecommendation] = useState<string>(appendix.recommendation || 'new');

    if (appendix.isNew) {
        return <>New</>;
    }

    const handleRecommendationChange = (option: any) => {
        const newRecommendation = option.value;
        setRecommendation(newRecommendation);
        editExistingAppendix({
            ...appendix,
            recommendation: newRecommendation as InsuranceAppendixRecommendation,
        });
    };

    return (
        <>
            {/* <select onChange={handleRecommendationChange} value={recommendation} onClick={(e) => e.stopPropagation()}>
                <option value="">---</option>
                {Object.values(InsuranceAppendixRecommendation).map((value) => (
                    <option key={value} value={value}>
                        {camelCaseToText(value)}
                    </option>
                ))}
            </select> */}
            <Select
                closeMenuOnSelect={false}
                styles={{
                    menu: (base) => ({
                        ...base,
                        position: 'absolute',
                        zIndex: 9999,
                    }),
                }}
                value={{
                    label: recommendation ? camelCaseToText(recommendation) : '---',
                    value: recommendation || '',
                }}
                name="recommendation"
                options={[
                    { label: '---', value: '' },
                    ...Object.values(InsuranceAppendixRecommendation).map((recommendation) => ({
                        label: camelCaseToText(recommendation),
                        value: recommendation,
                    })),
                ]}
                onChange={handleRecommendationChange}
            />
        </>
    );
};

export const AppendixCancellationColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    const [isCancelled, setIsCancelled] = useState<boolean>(appendix.isCancelled || false);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const isCancelled = e.target.checked;
        setIsCancelled(isCancelled);
        editExistingAppendix({
            ...appendix,
            isCancelled,
        });
    };

    return <FormInput type="checkbox" name="isCancelled" checked={isCancelled} onChange={onChange} disabled={false} />;
};

export const AppendixCoverageAmountColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    if (!appendix.riskMonthlyAmount && !appendix.riskCoverageAmount && !appendix.cancerCoverageAmount && !appendix.criticalIllnessCoverageAmount) {
        return noDataFound();
    }

    return !appendix.isNew ? (
        <span>
            {appendix.riskCoverageAmount && <MoneyColumn cell={{ value: appendix.riskCoverageAmount }} />}
            {appendix.riskMonthlyAmount && <MoneyColumn cell={{ value: appendix.riskMonthlyAmount }} />}
            {appendix.cancerCoverageAmount && <MoneyColumn cell={{ value: appendix.cancerCoverageAmount }} />}
            {appendix.criticalIllnessCoverageAmount && <MoneyColumn cell={{ value: appendix.criticalIllnessCoverageAmount }} />}
        </span>
    ) : (
        <span>
            {appendix.riskCoverageAmount && <MoneyColumn cell={{ value: appendix.riskCoverageAmount }} />}
            {appendix.cancerCoverageAmount && <MoneyColumn cell={{ value: appendix.cancerCoverageAmount }} />}
            {appendix.criticalIllnessCoverageAmount && <MoneyColumn cell={{ value: appendix.criticalIllnessCoverageAmount }} />}
        </span>
    );
};

export const AppendixPremiumColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    if (!appendix.premium && appendix.premium !== 0) {
        return noDataFound();
    }

    const premiumDiscountData = appendix.premiumDiscountData;
    const isPremiumDiscountDataAvailable = premiumDiscountData && typeof premiumDiscountData === 'object';
    const premiumDiscountDataTooltip = isPremiumDiscountDataAvailable ? (
        <Tooltip id="premiumDiscountData-tooltip">
            Before Discount: <span className="text-success">{premiumDiscountData.premiumBeforeDiscount || 'N/A'}</span>
            <br />
            Discount Term: <span className="text-success">{premiumDiscountData.discountTerm || 'N/A'}</span>
            <br />
            Discount Percent: <span className="text-success">{premiumDiscountData.currentDiscountPercent || 'N/A'}</span>
            <br />
            Discount Explanation: <span className="text-success">{premiumDiscountData.discountExplanation || 'N/A'}</span>
            <br />
            {/* Discount Issue Date: <span className='text-success'>{formatDate(appendix.issueDate, true) || 'N/A'}</span> */}
            <br />
            After Discount: <span className="text-success">{premiumDiscountData.premiumAfterDiscount || 'N/A'}</span>
        </Tooltip>
    ) : (
        <Tooltip id="premiumDiscountData-tooltip">No Discount Data Available</Tooltip>
    );

    return (
        <OverlayTrigger placement="top" overlay={premiumDiscountDataTooltip}>
            <span className="cursor-pointer">
                <MoneyColumn cell={{ value: appendix.premium }} />
            </span>
        </OverlayTrigger>
    );
};

export const PolicyCoverageAmountColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
    return policy?.totalCoverage ? <>₪{addCommas(policy?.totalCoverage || 0)}</> : <></>;
};

export const PolicyPremiumColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const coverageAppendix = policy.appendices?.find(
        (appendix) => appendix.riskCoverageAmount || appendix.cancerCoverageAmount || appendix.criticalIllnessCoverageAmount,
    );
    const coverageAmount =
        coverageAppendix?.riskCoverageAmount || coverageAppendix?.cancerCoverageAmount || coverageAppendix?.criticalIllnessCoverageAmount;
    if (!Number.isFinite(policy.totalPremium) && !Number.isFinite(coverageAmount)) {
        return noDataFound();
    }
    return (
        <>
            ₪{addCommas(policy.totalPremium)}{' '}
            {policy.premiumDiscountPercentage ? <span className="text-success">(הנחה {policy.premiumDiscountPercentage}%)</span> : null}
            {coverageAmount ? <div className="text">(₪{addCommas(coverageAmount)})</div> : null}
        </>
    );
};

export const PolicyTermColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (policy.insuranceStartDate && policy.insuranceEndDate) {
        return (
            <OverlayTrigger
                placement="bottom"
                overlay={
                    <Tooltip id="">
                        {formatDate(new Date(policy.insuranceStartDate), true)} - {formatDate(new Date(policy.insuranceEndDate), true)}
                    </Tooltip>
                }
            >
                <span className="cursor-pointer">
                    {new Date(policy.insuranceEndDate).getFullYear()} - {new Date(policy.insuranceStartDate).getFullYear()}
                </span>
            </OverlayTrigger>
        );
    } else if (policy.insuranceStartDate) {
        return <>{formatDate(new Date(policy.insuranceStartDate), true)} - </>;
    } else if (policy.insuranceEndDate) {
        return <>- {formatDate(new Date(policy.insuranceEndDate), true)}</>;
    }
    return <></>;
};

export const PolicyPremiumDiscountPercentageColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (!policy.premiumDiscountPercentage && policy.premiumDiscountPercentage !== 0) {
        return noDataFound();
    }
    return <>{policy.premiumDiscountPercentage}%</>;
};

export const AppendixPremiumTermColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    if (!appendix.premiumTerm) {
        return noDataFound();
    }
    return <>{appendix.premiumTerm}</>;
};

export const PolicyPremiumTermColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (!policy.premiumTerm) {
        return noDataFound();
    }
    return <>{camelCaseToText(policy.premiumTerm)}</>;
};

export const AppendixStartDateColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    if (!appendix.insuranceStartDate) {
        return noDataFound();
    }
    return <>{formatDate(new Date(appendix.insuranceStartDate), true)}</>;
};

export const PolicyStartDateColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (!policy.insuranceStartDate) {
        return noDataFound();
    }
    return <>{formatDate(new Date(policy.insuranceStartDate), true)}</>;
};

export const PolicyEndDateColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (!policy.insuranceEndDate) {
        return noDataFound();
    }
    return <>{formatDate(new Date(policy.insuranceEndDate), true)}</>;
};

export const PolicyIssueDateColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    if (!policy.issueDate) {
        return noDataFound();
    }
    return <>{formatDate(new Date(policy.issueDate), true)}</>;
};

export const PolicyCancellationAppendixCColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const { insurancePolicies, setInsurancePolicies } = useProductsContext();
    const [cancellationAppendixC, setCancellationAppendixC] = useState<boolean>(policy.cancellationAppendixC || false);

    if (policy.isNewPolicy) {
        return <></>;
    }

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const newCancellationAppendixC = event.target.checked;
        try {
            await editInsurancePolicy(policy._id!, {
                cancellationAppendixC: newCancellationAppendixC,
            });
            setCancellationAppendixC(newCancellationAppendixC);
            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                insurancePolicy._id === policy._id
                    ? {
                          ...insurancePolicy,
                          cancellationAppendixC: newCancellationAppendixC,
                      }
                    : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies);
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    return (
        <FormInput
            type="checkbox"
            name="cancellationAppendixC"
            checked={cancellationAppendixC}
            onChange={handleChange}
            onClick={(e: any) => e.stopPropagation()}
            className="d-flex align-items-center"
        />
    );
};

export const PolicyCommentColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const { insurancePolicies, setInsurancePolicies } = useProductsContext();
    const [comment, setComment] = useState<string>(policy.comment || '');

    if (policy.isNewPolicy) {
        return <></>;
    }

    const onSave = async (value: string) => {
        try {
            await editInsurancePolicy(policy._id!, { comment: value });
            setComment(value);

            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                insurancePolicy._id === policy._id
                    ? {
                          ...insurancePolicy,
                          comment: value,
                      }
                    : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies);
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    return <AutoSavingInput type="textarea" containerClass="" value={comment} name="comment" className={''} onSave={onSave} />;
};

export const PolicyCancellationAppendixAColumn = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const { insurancePolicies, setInsurancePolicies } = useProductsContext();
    const [cancellationAppendixA, setCancellationAppendixA] = useState<boolean>(policy.cancellationAppendixA || false);

    if (policy.isNewPolicy) {
        return <></>;
    }

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const newCancellationAppendixA = event.target.checked;
        try {
            await editInsurancePolicy(policy._id!, {
                cancellationAppendixA: newCancellationAppendixA,
            });
            setCancellationAppendixA(newCancellationAppendixA);
            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                insurancePolicy._id === policy._id
                    ? {
                          ...insurancePolicy,
                          cancellationAppendixA: newCancellationAppendixA,
                      }
                    : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies);
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    return (
        <FormInput
            type="checkbox"
            name="cancellationAppendixA"
            checked={cancellationAppendixA}
            onChange={handleChange}
            onClick={(e: any) => e.stopPropagation()}
            className="d-flex align-items-center"
        />
    );
};

export const PolicyNoCancellationRequired = ({ row: { original: policy } }: PolicyHolderColumnProps) => {
    const { insurancePolicies, setInsurancePolicies } = useProductsContext();
    const [noCancellationRequired, setNoCancellationRequired] = useState<boolean>(policy.noCancellationRequired || false);

    if (policy.isNewPolicy) {
        return <></>;
    }

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const newNoCancellationRequired = event.target.checked;
        try {
            await editInsurancePolicy(policy._id!, {
                noCancellationRequired: newNoCancellationRequired,
            });
            setNoCancellationRequired(newNoCancellationRequired);
            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                insurancePolicy._id === policy._id
                    ? {
                          ...insurancePolicy,
                          noCancellationRequired: newNoCancellationRequired,
                      }
                    : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies);
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    return (
        <FormInput
            type="checkbox"
            name="noCancellationRequired"
            checked={noCancellationRequired}
            onChange={handleChange}
            onClick={(e: any) => e.stopPropagation()}
            className="d-flex align-items-center"
        />
    );
};

export const AppendixBranchColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    const { t } = useTranslation('insurance');
    if (!appendix.branch) {
        return noDataFound();
    }
    return <>{t(camelCaseToText(appendix.branch))}</>;
};

export const AppendixBranchSectionColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    const { t } = useTranslation('insurance');
    if (!appendix.branchSection) {
        return noDataFound();
    }
    return <>{t(camelCaseToText(appendix.branchSection))}</>;
};

export const AppendixReplaceAppendixIdColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    if (!appendix.replaceAppendix) {
        return noDataFound();
    }
    return <>{`. . .${appendix.replaceAppendix.slice(-5)}`}</>;
};

export const AppendixIdColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    if (!appendix._id) {
        return noDataFound();
    }
    return <>{`. . .${appendix._id.slice(-5)}`}</>;
};

export const PolicyIdColumn = ({ row: { original: appendix } }: PolicyHolderColumnProps) => {
    if (!appendix._id) {
        return noDataFound();
    }
    return <>{`. . .${appendix._id.slice(-5)}`}</>;
};

export const AppendixInsuredPersonColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
    const insuredPerson = appendix.insuredPerson;
    if (!insuredPerson) {
        return noDataFound();
    }

    return insuredPerson.name || insuredPerson.idNumber;
};

export const PolicyActionsColumn = ({ row }: { row: any }) => {
    const { t } = useTranslation('insurance');
    const { insurancePolicies, setInsurancePolicies, createInsuranceProduct, initiallyExpandedRows, setInitiallyExpandedRows } = useProductsContext();

    const policy = row.original as InsurancePolicy;
    const [showEditPolicyModal, setShowEditPolicyModal] = useState(false);
    const [updateExpandedRows, setUpdateExpandedRows] = useState(false);
    const [isDownloadingPricing, setIsDownloadingPricing] = useState(false);

    const handleEditPolicy = async (modifiedStateOfPolicy: InsurancePolicy) => {
        try {
            const response = await editInsurancePolicy(policy._id!, modifiedStateOfPolicy);
            const editedInsurancePolicy = response.policy;
            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                insurancePolicy._id === editedInsurancePolicy._id ? editedInsurancePolicy : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies);
            return editedInsurancePolicy;
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    useEffect(() => {
        if (updateExpandedRows) {
            setInitiallyExpandedRows((prev) => [...prev, row.id]);
            setUpdateExpandedRows(false);
        }
    }, [updateExpandedRows, row.id]);

    const restorePolicy = async () => {
        try {
            const response = await restoreInsurancePolicy(policy._id);
            const editedInsurancePolicy = response.policy;
            // find the policy in the insurancePolicies array, change the recommendation to noChange, isAgentAppointed to false, and all the appendices to no cancellation
            const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
                insurancePolicy._id === editedInsurancePolicy._id
                    ? {
                          ...insurancePolicy,
                          recommendation: InsuranceRecommendation.noChange,
                          isAgentAppointed: false,
                          appendices: insurancePolicy.appendices?.map((appendix) => ({ ...appendix, isCancelled: false })),
                      }
                    : insurancePolicy,
            );
            setInsurancePolicies(newInsurancePolicies);
            return editedInsurancePolicy;
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    const deletePolicy = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const confirmation = await Swal.fire({
            title: 'Are you sure you want to delete this policy?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        });
        if (confirmation.isConfirmed) {
            try {
                await deleteInsurancePolicy(policy._id!);
                setInsurancePolicies(insurancePolicies.filter((insurancePolicy) => insurancePolicy._id !== policy._id));
            } catch (error) {
                globalErrorHandler(error);
            }
        }
    };

    const selectPolicy = async (isSelected: boolean) => {
        const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
            insurancePolicy._id === policy._id
                ? {
                      ...insurancePolicy,
                      isSelected,
                  }
                : insurancePolicy,
        );
        setInsurancePolicies(newInsurancePolicies);
    };

    const isRestoreDisplayed = () => {
        // if (policy.isNewPolicy) return false;
        // if (policy.status !== InsurancePolicyStatuses.Initial) return false;
        if (policy.isAgentAppointed) return true;
        if (policy.recommendation !== InsuranceRecommendation.noChange) return true;

        return false;
    };

    const handleDownloadPricing = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!policy._id) return;

        setIsDownloadingPricing(true);
        try {
            const response = await generateInsurancePricing({ policyId: policy._id });
            const fileName = `pricing_${policy._id}_${new Date().toLocaleDateString()}`;
            downloadFileFromAPI(response, fileName, 'pdf');
        } catch (error) {
            globalErrorHandler(error);
        } finally {
            setIsDownloadingPricing(false);
        }
    };

    return disabled(policy) ? (
        <></>
    ) : (
        <>
            {policy.isNewPolicy && (
                <OverlayTrigger placement="top" overlay={<Tooltip id="">{t('Edit policy')}</Tooltip>}>
                    <i className="mdi mdi-square-edit-outline cursor-pointer fs-3" onClick={() => setShowEditPolicyModal(true)}></i>
                </OverlayTrigger>
            )}
            {policy.isNewPolicy && (
                <OverlayTrigger placement="top" overlay={<Tooltip id="">{t('Download Pricing')}</Tooltip>}>
                    <i
                        className={`mdi ${isDownloadingPricing ? 'mdi-loading mdi-spin' : 'mdi-file-download-outline'} cursor-pointer fs-3`}
                        onClick={handleDownloadPricing}
                    ></i>
                </OverlayTrigger>
            )}
            {policy.isNewPolicy && (
                <OverlayTrigger placement="top" overlay={<Tooltip id="">{t('Delete policy')}</Tooltip>}>
                    <i className="mdi mdi-delete cursor-pointer fs-3" onClick={deletePolicy}></i>
                </OverlayTrigger>
            )}

            {!policy.isNewPolicy && (
                <OverlayTrigger placement="top" overlay={<Tooltip id="">{t('Select to replace')}</Tooltip>}>
                    <input
                        name="isSelected"
                        type="checkbox"
                        className="form-check-input is-selected"
                        checked={policy.isSelected}
                        onChange={(e: any) => selectPolicy(e.target.checked)}
                    />
                </OverlayTrigger>
            )}

            {isRestoreDisplayed() && (
                <OverlayTrigger placement="top" overlay={<Tooltip id="">{t('Back to origin')}</Tooltip>}>
                    <i className="mdi mdi-restore cursor-pointer fs-3" onClick={restorePolicy}></i>
                </OverlayTrigger>
            )}

            {showEditPolicyModal && (
                <InsurancePolicyModal
                    mode="edit"
                    show={showEditPolicyModal}
                    onHide={() => setShowEditPolicyModal(false)}
                    currentPolicyState={policy}
                    policyUpdateHandler={handleEditPolicy}
                />
            )}
        </>
    );
};
